import { useGetBill } from '@features/bills/hooks/useGetBill'
import { useFetchDaybookTransaction } from '@features/daybook/hooks/useFetchDaybookTransaction'
import { useFetchExternalInvoiceFromBilly } from '@features/invoices/hooks/useFetchExternalInvoiceFromBilly'
import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'
import { useFetchBankPayment } from '@features/transactions/hooks/useFetchBankPayment'
import { useFetchContactBalancePayment } from '@features/transactions/hooks/useFetchContactBalancePayment'
import { useFetchInvoiceLateFee } from '@features/transactions/hooks/useFetchInvoiceLateFee'
import { useFetchVatPayment } from '@features/vatDeclarations/hooks/useFetchVatPayment'
import { useVatDeclaration } from '@features/vatDeclarations/hooks/useVatDeclaration'

import { useInvoice } from '../../../../invoices/hooks/useInvoice'
import { OriginatorResult } from '../types/originatorResult'

interface UseFetchTransactionOriginatorParams {
  originatorType?: TransactionOriginatorType
  originatorId?: string
}

export const useFetchTransactionOriginator = ({
  originatorType,
  originatorId,
}: UseFetchTransactionOriginatorParams): OriginatorResult => {
  const isBill = originatorType === TransactionOriginatorType.Bill
  const isInvoice = originatorType === TransactionOriginatorType.Invoice
  const isExternalInvoice = originatorType === TransactionOriginatorType.ExternalInvoice
  const isVatReturn = originatorType === TransactionOriginatorType.SalesTaxReturn
  const isVatPayment = originatorType === TransactionOriginatorType.SalesTaxPayment
  const isBankPayment = originatorType === TransactionOriginatorType.BankPayment
  const isDaybookTransaction = originatorType === TransactionOriginatorType.DaybookTransaction
  const isContactBalancePayment = originatorType === TransactionOriginatorType.ContactBalancePayment
  const isInvoiceLateFee = originatorType === TransactionOriginatorType.InvoiceLateFee
  const isConversionBalance = originatorType === TransactionOriginatorType.ConversionBalance

  const { data: billData, isLoading: isBillLoading } = useGetBill(isBill ? originatorId : undefined, {
    refetchOnMount: false,
  })

  const { data: invoiceData, isLoading: isInvoiceLoading } = useInvoice(isInvoice ? originatorId : undefined, true)

  const { externalInvoice, isLoading: isExternalInvoiceLoading } = useFetchExternalInvoiceFromBilly(
    isExternalInvoice ? (originatorId ?? '') : '',
  )

  const { vatDeclaration, isLoading: isVatReturnLoading } = useVatDeclaration({
    vatDeclarationId: isVatReturn ? originatorId : undefined,
  })

  const { vatPayment, isLoading: isVatPaymentLoading } = useFetchVatPayment(isVatPayment ? originatorId : undefined)

  const { bankPayment, isLoading: isBankPaymentLoading } = useFetchBankPayment(isBankPayment ? originatorId : undefined)

  const { daybookTransaction, isLoading: isDaybookTransactionLoading } = useFetchDaybookTransaction(
    isDaybookTransaction ? originatorId : undefined,
  )

  const { contactBalancePayment, isLoading: isContactBalancePaymentLoading } = useFetchContactBalancePayment(
    isContactBalancePayment ? originatorId : undefined,
  )

  const { invoiceLateFee, isLoading: isInvoiceLateFeeLoading } = useFetchInvoiceLateFee(
    isInvoiceLateFee ? originatorId : undefined,
  )

  if (isBill) {
    return { type: TransactionOriginatorType.Bill, originator: billData?.bill, isLoading: isBillLoading }
  }
  if (isInvoice) {
    return {
      type: TransactionOriginatorType.Invoice,
      originator: invoiceData?.invoice,
      isLoading: isInvoiceLoading,
    }
  }
  if (isExternalInvoice) {
    return {
      type: TransactionOriginatorType.ExternalInvoice,
      originator: externalInvoice,
      isLoading: isExternalInvoiceLoading,
    }
  }
  if (isVatReturn) {
    return {
      type: TransactionOriginatorType.SalesTaxReturn,
      originator: vatDeclaration,
      isLoading: isVatReturnLoading,
    }
  }
  if (isVatPayment) {
    return {
      type: TransactionOriginatorType.SalesTaxPayment,
      originator: vatPayment,
      isLoading: isVatPaymentLoading,
    }
  }
  if (isBankPayment) {
    return {
      type: TransactionOriginatorType.BankPayment,
      originator: bankPayment,
      isLoading: isBankPaymentLoading,
    }
  }
  if (isDaybookTransaction) {
    return {
      type: TransactionOriginatorType.DaybookTransaction,
      originator: daybookTransaction,
      isLoading: isDaybookTransactionLoading,
    }
  }
  if (isContactBalancePayment) {
    return {
      type: TransactionOriginatorType.ContactBalancePayment,
      originator: contactBalancePayment,
      isLoading: isContactBalancePaymentLoading,
    }
  }
  if (isInvoiceLateFee) {
    return {
      type: TransactionOriginatorType.InvoiceLateFee,
      originator: invoiceLateFee,
      isLoading: isInvoiceLateFeeLoading,
    }
  }
  if (isConversionBalance) {
    /* Legacy data type - we don't require to fetch any data for originator as transaction itself has the same data */
    return {
      type: TransactionOriginatorType.ConversionBalance,
      originator: [],
      isLoading: false,
    }
  }

  return { type: undefined, originator: undefined, isLoading: false }
}
